import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { SpotMonthCount, SpotYearCount } from '../../types/api';

interface Props {
  monthlyCounts: SpotMonthCount[];
  yearlyCounts: SpotYearCount[];
}

const TEAL = '#0d9488';
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildMonthData(monthlyCounts: SpotMonthCount[]) {
  const lookup = new Map(monthlyCounts.map((m) => [`${m.year}-${m.month}`, m.count]));
  const now = new Date();
  const result = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
    result.push({
      label: `${MONTH_NAMES[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`,
      count: lookup.get(key) ?? 0,
    });
  }
  return result;
}

export function CatchesOverTimeChart({ monthlyCounts, yearlyCounts }: Props) {
  const [view, setView] = useState<'month' | 'year'>('month');

  const monthData = buildMonthData(monthlyCounts);
  const yearData = yearlyCounts.map((y) => ({ label: String(y.year), count: y.count }));
  const chartData = view === 'month' ? monthData : yearData;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">Catches Over Time</h2>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 text-xs font-medium">
          <button
            onClick={() => setView('month')}
            className={[
              'px-3 py-1 transition-colors cursor-pointer',
              view === 'month'
                ? 'bg-teal-600 dark:bg-teal-500 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700',
            ].join(' ')}
          >
            Month
          </button>
          <button
            onClick={() => setView('year')}
            className={[
              'px-3 py-1 transition-colors cursor-pointer',
              view === 'year'
                ? 'bg-teal-600 dark:bg-teal-500 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700',
            ].join(' ')}
          >
            Year
          </button>
        </div>
      </div>
      {chartData.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No data yet.</p>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} margin={{ top: 0, right: 8, bottom: 0, left: -16 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={view === 'month' ? 1 : 0} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value) => [value, 'Catches']}
              contentStyle={{ fontSize: 12 }}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {chartData.map((_, i) => (
                <Cell key={i} fill={TEAL} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
